import { useMemo, useState } from 'react';
import { Segmented } from '../components/data';
import { Field, PageHead } from '../components/ui';
import { AutotaskErd } from './AutotaskErd';
import { RecordDrawer, RecordList } from './AutotaskRecords';
import map from '../lib/autotaskEntities.json';

interface EntityField {
  name: string;
  type: string;
  readOnly: boolean;
  required: boolean;
  reference: string | null;
  picklist: boolean;
}
interface Entity {
  name: string;
  description: string;
  canQuery: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  fields: EntityField[];
}

const ENTITIES = (map as { entities: Entity[] }).entities;
const BY_NAME = new Map(ENTITIES.map((e) => [e.name, e]));

/* Where each entity sits, for the overview. Anything not listed lands in
   "Everything else" so a new entity in the data is never lost. */
const AREAS: { name: string; blurb: string; entities: string[] }[] = [
  { name: 'Clients & people', blurb: 'Who you work for and who does the work.', entities: ['Companies', 'CompanyLocations', 'CompanyNotes', 'Contacts', 'Resources', 'Roles', 'Departments', 'Countries'] },
  { name: 'Service desk', blurb: 'Tickets and the time and costs logged against them.', entities: ['Tickets', 'TicketNotes', 'TicketCharges', 'TimeEntries', 'ServiceCalls', 'Appointments'] },
  { name: 'Projects', blurb: 'Project work, broken into phases and tasks.', entities: ['Projects', 'Phases', 'Tasks'] },
  { name: 'Contracts & billing', blurb: 'What a client is signed up for, and what has been posted and invoiced.', entities: ['Contracts', 'ContractServices', 'ContractBlocks', 'ContractCharges', 'Services', 'ServiceBundles', 'BillingCodes', 'BillingItems', 'Invoices'] },
  { name: 'Sales', blurb: 'Opportunities, the quotes that price them, and orders.', entities: ['Opportunities', 'Quotes', 'QuoteItems', 'SalesOrders', 'Products'] },
  { name: 'Assets', blurb: 'The client devices and software you manage (Autotask calls them configuration items).', entities: ['ConfigurationItems', 'ConfigurationItemTypes', 'ConfigurationItemCategories'] },
  { name: 'Purchasing & expenses', blurb: 'What you buy, and what staff spend.', entities: ['PurchaseOrders', 'PurchaseOrderItems', 'ExpenseReports', 'ExpenseItems'] },
  { name: 'Feedback', blurb: "Autotask's built-in ticket surveys.", entities: ['Surveys', 'SurveyResults'] },
];

/* What MSP-OPS reads today, and where — so the map says which parts matter here. */
const USED_BY_APP: Record<string, string> = {
  Companies: 'Client links, Import from Autotask, SDF',
  Contacts: 'Review ticket slides (who raised it), Assets',
  Resources: 'Ticket lists, Quotes, Assets',
  Tickets: 'Ticket Audit, kill ratio, open tickets, review slides',
  TimeEntries: 'Hours worked on open tickets',
  BillingItems: 'SDF profitability',
  BillingCodes: 'Ticket Audit work types',
  ConfigurationItems: 'Client Assets tab',
  ConfigurationItemCategories: 'Client Assets tab',
  Products: 'Client Assets tab',
  Opportunities: 'Quotes report',
  Quotes: 'Quotes report',
  SurveyResults: 'Service Delivery CSAT',
};

/* The core of the diagram: where each box sits on a 1000 × 560 canvas.
   The lines between them are drawn from the data — a line wherever one has a
   field that references the other. */
const CORE: Record<string, [number, number]> = {
  Companies: [500, 250],
  Contacts: [250, 120],
  CompanyLocations: [250, 250],
  ConfigurationItems: [250, 400],
  Tickets: [750, 120],
  TimeEntries: [890, 260],
  Resources: [750, 400],
  Projects: [500, 60],
  Tasks: [640, 20],
  Contracts: [500, 470],
  BillingItems: [890, 470],
  Invoices: [890, 540],
  Opportunities: [100, 470],
  Quotes: [100, 340],
  Products: [100, 200],
  SurveyResults: [640, 540],
};

function links(e: Entity) {
  return e.fields.filter((f) => f.reference);
}

export function AutotaskMapPage() {
  const [selected, setSelected] = useState<string>('Tickets');
  const [search, setSearch] = useState('');
  /* Everything links to Companies and Resources, so drawing every line makes
     a tangle. By default only the selected entity's links are drawn. */
  const [allLinks, setAllLinks] = useState(false);
  /* Fields, or the entity's live records from Autotask. */
  const [view, setView] = useState<'fields' | 'records'>('fields');
  /* The records opened, in order — the drawer's trail back. */
  const [stack, setStack] = useState<{ entity: string; id: number }[]>([]);
  /* The overview and area chips, or the full database diagram with every connector. */
  const [mode, setMode] = useState<'overview' | 'diagram'>('overview');
  const openRecord = (e: string, id: number) => setStack((s) => [...s, { entity: e, id }]);
  const entity = BY_NAME.get(selected) ?? ENTITIES[0];

  const linkedFrom = useMemo(
    () =>
      ENTITIES.flatMap((e) => e.fields.filter((f) => f.reference === entity?.name).map((f) => ({ entity: e.name, field: f.name }))),
    [entity],
  );

  const fields = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (entity?.fields ?? []).filter((f) => !q || f.name.toLowerCase().includes(q) || (f.reference ?? '').toLowerCase().includes(q));
  }, [entity, search]);

  const coreLines = useMemo(() => {
    const seen = new Set<string>();
    const out: { a: string; b: string }[] = [];
    for (const name of Object.keys(CORE)) {
      const e = BY_NAME.get(name);
      if (!e) continue;
      for (const f of links(e)) {
        const target = f.reference!;
        if (!CORE[target] || target === name) continue;
        const key = [name, target].sort().join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ a: name, b: target });
      }
    }
    return out;
  }, []);

  const placed = new Set(AREAS.flatMap((a) => a.entities));
  const others = ENTITIES.map((e) => e.name).filter((n) => !placed.has(n));
  const areas = others.length ? [...AREAS, { name: 'Everything else', blurb: '', entities: others }] : AREAS;
  const totalFields = ENTITIES.reduce((n, e) => n + e.fields.length, 0);

  const pick = (name: string) => {
    if (!BY_NAME.has(name)) return;
    setSelected(name);
    setSearch('');
  };
  /* From the chips and field links, also bring the field list into view. */
  const open = (name: string) => {
    pick(name);
    document.getElementById('atmap-entity')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <PageHead
        title="Autotask Data Map"
        lede={`How Autotask's data is structured — ${ENTITIES.length} core entities (Autotask's name for its tables) and their ${totalFields.toLocaleString()} fields, what each links to, and which ones MSP-OPS reads. From Autotask's REST API documentation.`}
        actions={
          <Segmented
            label="View"
            options={[
              { id: 'overview', label: 'Overview' },
              { id: 'diagram', label: 'Database diagram' },
            ]}
            value={mode}
            onChange={setMode}
          />
        }
      />

      {mode === 'diagram' && (
        <section className="card mb-4">
          <div className="card-head">
            <div className="grow">
              <h3>Database diagram</h3>
              <p className="sub">
                Every entity as a table, grouped by area, with a connector from each field that holds an id to the table it points to.
              </p>
            </div>
          </div>
          <div className="card-body">
            <AutotaskErd entities={ENTITIES} areas={areas} selected={selected} onSelect={open} used={USED_BY_APP} />
          </div>
        </section>
      )}

      {mode === 'overview' && (
      <>

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>How the core connects</h3>
            <p className="sub">
              A line means one holds an id pointing at the other — a ticket's companyID, a time entry's ticketID. Click a box
              to see what it connects to, and its fields below.
            </p>
          </div>
          <label className="checkbox small">
            <input type="checkbox" checked={allLinks} onChange={(e) => setAllLinks(e.target.checked)} />
            Show every link
          </label>
        </div>
        <div className="card-body">
          <svg viewBox="0 0 1000 580" className="atmap-diagram" role="img" aria-label="Autotask core entity relationships">
            {coreLines.filter(({ a, b }) => allLinks || a === selected || b === selected).map(({ a, b }) => {
              const [x1, y1] = CORE[a]!;
              const [x2, y2] = CORE[b]!;
              const on = a === selected || b === selected;
              return <line key={`${a}-${b}`} x1={x1} y1={y1} x2={x2} y2={y2} className={on ? 'atmap-line is-on' : 'atmap-line'} />;
            })}
            {Object.entries(CORE).map(([name, [x, y]]) =>
              BY_NAME.has(name) ? (
                <g key={name} className={`atmap-node${name === selected ? ' is-on' : ''}${USED_BY_APP[name] ? ' is-used' : ''}`} onClick={() => pick(name)}>
                  <rect x={x - 70} y={y - 18} width={140} height={36} rx={8} />
                  <text x={x} y={y + 5} textAnchor="middle">
                    {name}
                  </text>
                </g>
              ) : null,
            )}
          </svg>
          <p className="xs muted">
            <span className="atmap-key is-used" /> MSP-OPS reads it today · <span className="atmap-key" /> not yet
          </p>
        </div>
      </section>

      <div className="atmap-areas mb-4">
        {areas.map((area) => (
          <section key={area.name} className="card">
            <div className="card-body">
              <h3 className="atmap-area-h">{area.name}</h3>
              {area.blurb && <p className="xs muted mb-2">{area.blurb}</p>}
              <div className="atmap-chips">
                {area.entities
                  .filter((n) => BY_NAME.has(n))
                  .map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`atmap-chip${n === selected ? ' is-on' : ''}${USED_BY_APP[n] ? ' is-used' : ''}`}
                      onClick={() => open(n)}
                      title={BY_NAME.get(n)!.description}
                    >
                      {n}
                      <span className="atmap-chip-n">{BY_NAME.get(n)!.fields.length}</span>
                    </button>
                  ))}
              </div>
            </div>
          </section>
        ))}
      </div>

      </>
      )}

      {entity && (
        <section className="card" id="atmap-entity">
          <div className="card-head">
            <div className="grow">
              <h3>{entity.name}</h3>
              <p className="sub">{entity.description}</p>
              <p className="xs muted mt-2">
                API: {[entity.canQuery && 'query', entity.canCreate && 'create', entity.canUpdate && 'update', entity.canDelete && 'delete'].filter(Boolean).join(' · ') || 'read-only'}
                {USED_BY_APP[entity.name] ? ` · used in MSP-OPS for ${USED_BY_APP[entity.name]}` : ''}
              </p>
            </div>
            <div className="row wrap" style={{ alignItems: 'flex-end' }}>
              <Field label="Show">
                <Segmented
                  label="Show"
                  options={[
                    { id: 'fields', label: 'Fields' },
                    { id: 'records', label: 'Live records' },
                  ]}
                  value={view}
                  onChange={setView}
                />
              </Field>
              {view === 'fields' && (
                <Field label="Find a field">
                  <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. date, company" />
                </Field>
              )}
            </div>
          </div>
          {view === 'records' && (
            <div className="card-body">
              <p className="xs muted mb-2">
                Read live from your Autotask. Open a record for every field; follow any id to the record it names, or list
                the records that point at it.
              </p>
              <RecordList entity={entity.name} onOpen={openRecord} />
            </div>
          )}
          {view === 'fields' && (
          <div className="card-body">
            <div className="grid grid-2 mb-4">
              <div>
                <div className="xs strong mb-2">Links to ({new Set(links(entity).map((f) => f.reference)).size})</div>
                <div className="atmap-chips">
                  {[...new Set(links(entity).map((f) => f.reference!))].sort().map((r) => (
                    <button key={r} type="button" className={`atmap-chip${BY_NAME.has(r) ? '' : ' is-off'}`} onClick={() => open(r)} disabled={!BY_NAME.has(r)}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="xs strong mb-2">Linked from ({new Set(linkedFrom.map((l) => l.entity)).size})</div>
                <div className="atmap-chips">
                  {[...new Set(linkedFrom.map((l) => l.entity))].sort().map((r) => (
                    <button key={r} type="button" className="atmap-chip" onClick={() => open(r)}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Points to</th>
                    <th>Required</th>
                    <th>Read-only</th>
                    <th>Picklist</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f) => (
                    <tr key={f.name}>
                      <td className="mono xs">{f.name}</td>
                      <td className="xs">{f.type}</td>
                      <td>
                        {f.reference ? (
                          BY_NAME.has(f.reference) ? (
                            <button type="button" className="link-button" onClick={() => open(f.reference!)}>
                              {f.reference}
                            </button>
                          ) : (
                            <span className="xs">{f.reference}</span>
                          )
                        ) : (
                          <span className="subtle">—</span>
                        )}
                      </td>
                      <td className="xs">{f.required ? 'Yes' : ''}</td>
                      <td className="xs">{f.readOnly ? 'Yes' : ''}</td>
                      <td className="xs">{f.picklist ? 'Yes' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="xs muted mt-2">
              A picklist field holds a number whose label comes from Autotask's setup; MSP-OPS looks the labels up live.
              User-defined fields are your own additions and are not listed here.
            </p>
          </div>
          )}
        </section>
      )}

      {stack.length > 0 && (
        <RecordDrawer
          stack={stack}
          onClose={() => setStack([])}
          onOpen={openRecord}
          onBack={(depth) => setStack((s) => s.slice(0, depth))}
        />
      )}
    </>
  );
}
